<script setup lang="ts">
import { useBillingUtils } from '@/use/billingUtils';
import { useToast } from '@/use/toast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import type { PaymentMethod as BillingPaymentMethod } from '@/types/billing';
import { Button, LayoutGap, Modal, useConfirmStore } from '@dabble/quillui';
import { computed, onMounted, ref } from 'vue';
import BillingPaymentMethodForm from '@/components/BillingPaymentMethodForm/BillingPaymentMethodForm.vue';
import BillingPaymentMethodRow from '@/components/BillingPaymentMethodRow/BillingPaymentMethodRow.vue';

const billingStore = useBillingStore();
const { t } = useI18n();
const { showToast } = useToast();
const { formatPrice } = useBillingUtils();
const confirmStore = useConfirmStore();

const paymentMethods = computed((): BillingPaymentMethod[] => billingStore.paymentMethods as BillingPaymentMethod[]);

const showAddPaymentModal = ref(false);

async function onAddPaymentDone() {
  showAddPaymentModal.value = false;
  await billingStore.fetchBillingOverview();

  const openInvoice = billingStore.latestOpenInvoice;
  if (!billingStore.isDelinquent || !openInvoice) return;

  const amount = formatPrice(openInvoice.total, openInvoice.currency, true);
  const confirmed = await confirmStore.confirm(
    t('billing_payment_retry_after_add_title'),
    t('billing_payment_retry_after_add_body', { amount }),
    {
      cancel: { text: t('billing_payment_retry_after_add_cancel') },
      confirm: { text: t('billing_payment_retry_after_add_confirm'), variant: 'primary' },
    },
  );
  if (!confirmed) return;

  try {
    const updated = await billingStore.retryInvoicePayment(openInvoice.id);
    if (updated?.status === 'paid') {
      showToast('success', t('billing_payment_method_retry_success'));
    }
  } catch (err) {
    logError('Failed to retry invoice after adding payment method:', err);
  }
}

function onAddPaymentCancel() {
  showAddPaymentModal.value = false;
}

onMounted(() => {
  if (billingStore.isLoading) return;
  if (billingStore.paymentMethods.length > 0) return;
  billingStore.fetchBillingOverview();
});
</script>

<template>
  <LayoutGap direction="column" size="small" class="billing-payment-method-list">
    <LayoutGap direction="column" size="small" class="payment-methods-list">
      <template v-if="billingStore.isLoading">
        <div>{{ t('payment_methods_loading') }}</div>
      </template>
      <template v-else-if="paymentMethods && paymentMethods.length > 0">
        <BillingPaymentMethodRow v-for="method in paymentMethods" :key="method.id" :method="method" />
      </template>
      <template v-else>
        <div class="no-payment-method">{{ t('billing_payment_method_none') }}</div>
      </template>
      <Button
        class="add-payment-method"
        icon-left="plus"
        variant="secondary"
        finish="ghost"
        @click="showAddPaymentModal = true"
      >
        {{ t('billing_payment_method_add') }}
      </Button>
    </LayoutGap>

    <Modal v-model="showAddPaymentModal" :title="t('billing_payment_method')" size="large" @close="onAddPaymentCancel">
      <BillingPaymentMethodForm v-if="showAddPaymentModal" @done="onAddPaymentDone" @cancel="onAddPaymentCancel" />
    </Modal>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.no-payment-method {
  padding: 1rem;
  text-align: center;
  color: #6b7280;
}

.add-payment-method {
  outline: none;
  border: 1px dashed var(--border-primary, #d1d5db);
  background: transparent;
  color: var(--text-primary, #1f2937);
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-radius: 0.375rem;
  transition:
    background-color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    border-color: #9ca3af;
  }
}
</style>
